import { useSearchParams } from 'react-router-dom';
import type { AnalyticsRange } from '../../../../shared/types/analytics';

const SINCE_VALUES: readonly AnalyticsRange[] = ['7d', '30d', '90d', 'all'];
const DEFAULT_SINCE: AnalyticsRange = '30d';

export interface InsightsFilters {
  since: AnalyticsRange;
  project: string | undefined;
  setSince: (s: AnalyticsRange) => void;
  setProject: (p: string | undefined) => void;
}

/**
 * UX-02 — the /insights hub's shared range + project filter. Both live in
 * the URL (`?since=`, `?project=`) so switching tabs (and reloading) keeps
 * the same values; each panel reads its slice from here instead of owning
 * its own range UI. `since` defaults to 30d — Health defaulted to 7d, Spend
 * to 30d, Efficiency to 90d and the Insights card to "all"; 30d was picked
 * as the one shared default (see UX-02-insights-hub.md "Outcome").
 */
export function useInsightsFilters(): InsightsFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSince = searchParams.get('since');
  const since: AnalyticsRange =
    rawSince && (SINCE_VALUES as readonly string[]).includes(rawSince)
      ? (rawSince as AnalyticsRange)
      : DEFAULT_SINCE;
  const project = searchParams.get('project') ?? undefined;

  const setSince = (next: AnalyticsRange) => {
    const p = new URLSearchParams(searchParams);
    if (next === DEFAULT_SINCE) p.delete('since');
    else p.set('since', next);
    setSearchParams(p, { replace: true });
  };

  const setProject = (next: string | undefined) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('project', next);
    else p.delete('project');
    setSearchParams(p, { replace: true });
  };

  return { since, project, setSince, setProject };
}
